import { API_CONFIG, BRAND_PRODUCTS } from "../config";

// Enhanced food analysis service with multiple API sources
export class FoodAnalysisService {
  // Analyze text input using multiple sources
  static async analyzeText(text) {
    console.log("🔍 Starting comprehensive food analysis for:", text);

    // Step 1: Check brand database first (fastest)
    const brandResult = this.checkBrandDatabase(text);
    if (brandResult) {
      console.log("✅ Found in brand database:", brandResult);
      return brandResult;
    }

    // Step 2: Try multiple APIs in parallel for better coverage
    const apiPromises = [
      this.analyzeWithSpoonacular(text),
      this.analyzeWithFoodDataCentral(text),
      this.analyzeWithOpenFoodFacts(text),
    ];

    try {
      const results = await Promise.allSettled(apiPromises);
      console.log("📊 API Results:", results);

      // Find the best result (highest confidence)
      const validResults = results
        .filter((result) => result.status === "fulfilled" && result.value)
        .map((result) => result.value);

      if (validResults.length > 0) {
        const bestResult = validResults.reduce((best, current) =>
          current.confidence > best.confidence ? current : best
        );
        console.log("🏆 Best result:", bestResult);
        return bestResult;
      }

      // Fallback if all APIs fail
      return this.createFallbackResult(text);
    } catch (error) {
      console.error("❌ Error in API analysis:", error);
      return this.createFallbackResult(text);
    }
  }

  // Analyze image using multiple vision APIs
  static async analyzeImage(imageFile) {
    console.log("📸 Starting image analysis for:", imageFile.name);

    const base64Image = await this.fileToBase64(imageFile);

    // Try Google Vision API first (most accurate)
    if (API_CONFIG.GOOGLE_VISION.enabled && API_CONFIG.GOOGLE_VISION.apiKey) {
      try {
        const googleResult = await this.analyzeWithGoogleVision(base64Image);
        if (googleResult) {
          console.log("✅ Google Vision result:", googleResult);
          return googleResult;
        }
      } catch (error) {
        console.warn("⚠️ Google Vision failed:", error);
      }
    }

    // Fallback to Spoonacular image analysis
    if (API_CONFIG.SPOONACULAR_IMAGE.enabled) {
      try {
        const spoonacularResult = await this.analyzeWithSpoonacularImage(
          base64Image
        );
        if (spoonacularResult) {
          console.log("✅ Spoonacular image result:", spoonacularResult);
          return spoonacularResult;
        }
      } catch (error) {
        console.warn("⚠️ Spoonacular image analysis failed:", error);
      }
    }

    // Ultimate fallback
    return {
      isSafe: false,
      confidence: 0.3,
      reason:
        "Unable to analyze image - please check ingredients manually or try text input",
      source: "Image Analysis Failed",
    };
  }

  // Brand database check (instant)
  static checkBrandDatabase(text) {
    const lowerText = text.toLowerCase();
    for (const [brand, data] of Object.entries(BRAND_PRODUCTS)) {
      if (lowerText.includes(brand)) {
        return {
          isSafe: data.isSafe,
          confidence: data.confidence,
          reason: data.reason,
          source: "Brand Database",
        };
      }
    }
    return null;
  }

  // Spoonacular text analysis
  static async analyzeWithSpoonacular(text) {
    if (!API_CONFIG.SPOONACULAR.enabled) return null;

    try {
      const response = await fetch(
        `${API_CONFIG.SPOONACULAR.baseUrl}/recipes/analyze?apiKey=${API_CONFIG.SPOONACULAR.apiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: text,
            ingredients: [text],
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const alphaGalSafe = this.checkAlphaGalSafety(data);

        return {
          isSafe: alphaGalSafe.isSafe,
          confidence: alphaGalSafe.confidence,
          reason: alphaGalSafe.reason,
          source: "Spoonacular API",
        };
      }
    } catch (error) {
      console.warn("⚠️ Spoonacular text analysis failed:", error);
    }
    return null;
  }

  // FoodData Central analysis
  static async analyzeWithFoodDataCentral(text) {
    if (!API_CONFIG.FOOD_DATA_CENTRAL.enabled) return null;

    try {
      const response = await fetch(
        `${API_CONFIG.FOOD_DATA_CENTRAL.baseUrl}/foods/search?api_key=${
          API_CONFIG.FOOD_DATA_CENTRAL.apiKey
        }&query=${encodeURIComponent(text)}&pageSize=5`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.foods && data.foods.length > 0) {
          const food = data.foods[0];
          const alphaGalSafe = this.analyzeFoodDataCentralFood(food);

          return {
            isSafe: alphaGalSafe.isSafe,
            confidence: alphaGalSafe.confidence,
            reason: alphaGalSafe.reason,
            source: "FoodData Central (USDA)",
          };
        }
      }
    } catch (error) {
      console.warn("⚠️ FoodData Central analysis failed:", error);
    }
    return null;
  }

  // OpenFoodFacts analysis
  static async analyzeWithOpenFoodFacts(text) {
    if (!API_CONFIG.OPEN_FOOD_FACTS.enabled) return null;

    try {
      const response = await fetch(
        `${API_CONFIG.OPEN_FOOD_FACTS.baseUrl}/${encodeURIComponent(text)}.json`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === 1 && data.product) {
          const alphaGalSafe = this.analyzeOpenFoodFactsProduct(data.product);

          return {
            isSafe: alphaGalSafe.isSafe,
            confidence: alphaGalSafe.confidence,
            reason: alphaGalSafe.reason,
            source: "OpenFoodFacts",
          };
        }
      }
    } catch (error) {
      console.warn("⚠️ OpenFoodFacts analysis failed:", error);
    }
    return null;
  }

  // Google Vision API analysis
  static async analyzeWithGoogleVision(base64Image) {
    if (!API_CONFIG.GOOGLE_VISION.enabled || !API_CONFIG.GOOGLE_VISION.apiKey)
      return null;

    try {
      const response = await fetch(
        `${API_CONFIG.GOOGLE_VISION.baseUrl}?key=${API_CONFIG.GOOGLE_VISION.apiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            requests: [
              {
                image: { content: base64Image.split(",")[1] },
                features: [
                  { type: "LABEL_DETECTION", maxResults: 10 },
                  { type: "TEXT_DETECTION", maxResults: 10 },
                ],
              },
            ],
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.responses && data.responses[0]) {
          const response = data.responses[0];
          const alphaGalSafe = this.analyzeGoogleVisionResponse(response);

          return {
            isSafe: alphaGalSafe.isSafe,
            confidence: alphaGalSafe.confidence,
            reason: alphaGalSafe.reason,
            source: "Google Vision API",
          };
        }
      }
    } catch (error) {
      console.warn("⚠️ Google Vision analysis failed:", error);
    }
    return null;
  }

  // Spoonacular image analysis
  static async analyzeWithSpoonacularImage(base64Image) {
    if (!API_CONFIG.SPOONACULAR_IMAGE.enabled) return null;

    try {
      const formData = new FormData();
      formData.append("file", base64Image);

      const response = await fetch(
        `${API_CONFIG.SPOONACULAR_IMAGE.baseUrl}/food/images/classify?apiKey=${API_CONFIG.SPOONACULAR_IMAGE.apiKey}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        const alphaGalSafe = this.analyzeSpoonacularImageResponse(data);

        return {
          isSafe: alphaGalSafe.isSafe,
          confidence: alphaGalSafe.confidence,
          reason: alphaGalSafe.reason,
          source: "Spoonacular Image API",
        };
      }
    } catch (error) {
      console.warn("⚠️ Spoonacular image analysis failed:", error);
    }
    return null;
  }

  // Helper methods for analyzing different API responses
  static checkAlphaGalSafety(data) {
    const unsafeIngredients = [
      "beef",
      "pork",
      "lamb",
      "venison",
      "gelatin",
      "milk",
      "cheese",
      "whey",
      "casein",
    ];
    const text = JSON.stringify(data).toLowerCase();

    for (const ingredient of unsafeIngredients) {
      if (text.includes(ingredient)) {
        return {
          isSafe: false,
          confidence: 0.9,
          reason: `Contains ${ingredient} - not Alpha-Gal safe`,
        };
      }
    }

    return {
      isSafe: true,
      confidence: 0.7,
      reason: "No obvious Alpha-Gal triggers detected",
    };
  }

  static analyzeFoodDataCentralFood(food) {
    const ingredients = food.ingredients || "";
    const unsafeIngredients = [
      "beef",
      "pork",
      "lamb",
      "venison",
      "gelatin",
      "milk",
      "cheese",
      "whey",
      "casein",
    ];

    for (const ingredient of unsafeIngredients) {
      if (ingredients.toLowerCase().includes(ingredient)) {
        return {
          isSafe: false,
          confidence: 0.85,
          reason: `USDA data shows ${ingredient} content - not Alpha-Gal safe`,
        };
      }
    }

    return {
      isSafe: true,
      confidence: 0.8,
      reason: "USDA nutritional data shows no Alpha-Gal triggers",
    };
  }

  static analyzeOpenFoodFactsProduct(product) {
    const ingredients = product.ingredients_text || "";
    const unsafeIngredients = [
      "beef",
      "pork",
      "lamb",
      "venison",
      "gelatin",
      "milk",
      "cheese",
      "whey",
      "casein",
    ];

    for (const ingredient of unsafeIngredients) {
      if (ingredients.toLowerCase().includes(ingredient)) {
        return {
          isSafe: false,
          confidence: 0.9,
          reason: `Product contains ${ingredient} - not Alpha-Gal safe`,
        };
      }
    }

    return {
      isSafe: true,
      confidence: 0.75,
      reason: "OpenFoodFacts database shows no Alpha-Gal triggers",
    };
  }

  static analyzeGoogleVisionResponse(response) {
    const labels = response.labelAnnotations || [];
    const texts = response.textAnnotations || [];

    const allText = [
      ...labels.map((l) => l.description),
      ...texts.map((t) => t.description),
    ]
      .join(" ")
      .toLowerCase();
    const unsafeIngredients = [
      "beef",
      "pork",
      "lamb",
      "venison",
      "gelatin",
      "milk",
      "cheese",
      "whey",
      "casein",
    ];

    for (const ingredient of unsafeIngredients) {
      if (allText.includes(ingredient)) {
        return {
          isSafe: false,
          confidence: 0.85,
          reason: `Google Vision detected ${ingredient} - not Alpha-Gal safe`,
        };
      }
    }

    return {
      isSafe: true,
      confidence: 0.6,
      reason: "Google Vision analysis shows no obvious Alpha-Gal triggers",
    };
  }

  static analyzeSpoonacularImageResponse(data) {
    const classifications = data.classifications || [];
    const allText = classifications
      .map((c) => c.title)
      .join(" ")
      .toLowerCase();
    const unsafeIngredients = [
      "beef",
      "pork",
      "lamb",
      "venison",
      "gelatin",
      "milk",
      "cheese",
      "whey",
      "casein",
    ];

    for (const ingredient of unsafeIngredients) {
      if (allText.includes(ingredient)) {
        return {
          isSafe: false,
          confidence: 0.8,
          reason: `Image shows ${ingredient} - not Alpha-Gal safe`,
        };
      }
    }

    return {
      isSafe: true,
      confidence: 0.7,
      reason: "Image analysis shows no obvious Alpha-Gal triggers",
    };
  }

  static createFallbackResult(text) {
    return {
      isSafe: false,
      confidence: 0.3,
      reason: "Unable to analyze - please check ingredients manually",
      source: "Analysis Failed",
    };
  }

  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }
}
